import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import AltRouteOutlinedIcon from '@mui/icons-material/AltRouteOutlined';
import { EmptyState, ListSkeleton, PageHeader, useNotify } from '@routes-plan/ui';
import { Office } from '../../../models/office';
import { Route } from '../../../models/route';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { ScreenTourButton } from '../../../components/help/ScreenHelp';
import { AdminPage, ContentCard, FilterRow, FormActions } from '../shared/AdminUi';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const NONE = 'none';

/** Ruta de un representante: tiene una sola; se elige la sede y luego la ruta. */
const SalesRepRoutes: React.FC<ShowAlerts> = () => {
  const t = useT();
  const notify = useNotify();
  const navigate = useNavigate();
  const { id } = useParams();
  const [selectedRoute, setSelectedRoute] = useState<number | null>(null);
  const [officeId, setOfficeId] = useState('');
  const [offices, setOffices] = useState<Office[]>([]);
  const [employeeOfficeIds, setEmployeeOfficeIds] = useState<number[]>([]);
  const [routes, setRoutes] = useState<Route[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  setPageTitle('salesReps.assignRoutesTitle');
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Sedes permitidas según los permisos del usuario.
        const { data: permittedOfficesData } = await axios.get('offices');
        setOffices(Array.isArray(permittedOfficesData?.data) ? permittedOfficesData.data : []);
        // Sedes asignadas hoy al representante (para precargar la selección).
        const { data: employeeOfficeData } = await axios.get(`users/${id}/offices`);
        const ids = Array.isArray(employeeOfficeData?.offices)
          ? employeeOfficeData.offices.map((o: Office) => Number(o.id)).filter((n: number) => Number.isFinite(n))
          : [];
        setEmployeeOfficeIds(ids);
      } catch (error) {
        notify.error(t('common.loadError'));
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const getUserRoutes = async (nextOfficeId: string) => {
    const numericOfficeId = Number(nextOfficeId);
    if (!Number.isFinite(numericOfficeId)) {
      setRoutes([]);
      setSelectedRoute(null);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`users/${id}/offices/${nextOfficeId}/routes`);
      const officeRoutes: Route[] = Array.isArray(data?.routes) ? data.routes : [];
      setRoutes(officeRoutes);
      // El representante tiene una sola ruta: se marca la que ya tiene en esta sede.
      const assigned = (Array.isArray(data?.selectedRoutes) ? data.selectedRoutes : [])
        .map((route: Route) => Number(route.id))
        .find((routeId: number) => officeRoutes.some((route) => Number(route.id) === routeId));
      setSelectedRoute(assigned ?? null);
    } catch (error) {
      notify.error(t('common.loadError'));
    } finally {
      setLoading(false);
    }
  };

  // Precarga la primera sede del representante (o la primera permitida).
  useEffect(() => {
    if (!offices || offices.length === 0) return;
    const defaultOfficeId = (employeeOfficeIds.length > 0 ? employeeOfficeIds[0] : Number(offices[0]?.id)) || null;
    if (defaultOfficeId && String(defaultOfficeId) !== officeId) {
      const next = String(defaultOfficeId);
      setOfficeId(next);
      getUserRoutes(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offices, employeeOfficeIds]);

  const submit = async () => {
    setSaving(true);
    try {
      await axios.put(`/users/${id}/routes`, { routeId: selectedRoute ? [selectedRoute] : [], userId: id, officeId });
      notify.success(t('salesReps.routeAssignedOk'));
      navigate('/admin/sales-reps');
    } catch (error) {
      notify.error(t('common.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const title = t('salesReps.assignRoutesTitle');
  return (
    <AdminPage narrow>
      <PageHeader
        title={title}
        description={t('salesReps.assignRoutesHint')}
        breadcrumbs={[{ label: t('salesReps.title'), href: '#/admin/sales-reps' }, { label: title }]}
        helpAction={<ScreenTourButton />}
      />
      <Box data-tour="sales-rep-routes-list">
        <ContentCard>
          <FilterRow sx={{ mb: 2 }}>
            <TextField
              id="rep-routes-office"
              data-tour="sales-rep-routes-office"
              select
              size="small"
              label={t('salesReps.office')}
              value={offices.length ? officeId : ''}
              onChange={(e) => {
                setOfficeId(e.target.value);
                getUserRoutes(e.target.value);
              }}
              fullWidth
            >
              {offices.map((office) => (
                <MenuItem key={office.id} value={String(office.id)}>
                  {office.name}
                </MenuItem>
              ))}
            </TextField>
          </FilterRow>
          {loading ? (
            <ListSkeleton rows={4} columns={1} />
          ) : routes.length === 0 ? (
            <EmptyState dense icon={<AltRouteOutlinedIcon />} title={officeId ? t('salesReps.noRoutesFound') : t('salesReps.noOffices')} />
          ) : (
            <RadioGroup
              value={selectedRoute ? String(selectedRoute) : NONE}
              onChange={(_, v) => setSelectedRoute(v === NONE ? null : Number(v))}
            >
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(240px, 100%), 1fr))', columnGap: 2 }}>
                <FormControlLabel value={NONE} control={<Radio />} label={t('salesReps.noRoute')} sx={{ minWidth: 0, mr: 0 }} />
                {routes.map((route) => (
                  <FormControlLabel key={route.id} value={String(route.id)} control={<Radio />} label={route.name} sx={{ minWidth: 0, mr: 0 }} />
                ))}
              </Box>
            </RadioGroup>
          )}
        </ContentCard>
      </Box>
      <FormActions cancelTo="/admin/sales-reps" submit={false} onSave={submit} saving={saving} disabled={!officeId} />
    </AdminPage>
  );
};

export default SalesRepRoutes;
